import { Column, Section } from '../models';

/**
 * Options object for doTransitiveSectionColumnsRelationsFiltering
 */
interface TransitiveFilteringOptions {
  /**
   * Final transition Map (source of Q => R)
   */
  transitiveColumnToSectionMap: { [key: number]: number };
  /**
   * Transition Map (source of P => Q)
   */
  transitiveColumnToColumnMap: { [key: number]: number };
}

/**
 * It gets two transitive maps, one for related column to principal column and
 * another for column to section, having these two maps then it filters out
 * columns from current sections where we apply a transitive operation.
 *
 * if related column points to a principal column
 * and the principal points to section
 * then related column should also point to section, if this is not true then
 * column is filtered out since it don't has to be displayed on current section
 * but where the "principal" points to
 *
 * @param sections
 * @returns
 */
export function doTransitiveSectionColumnsRelationsFiltering(sections: Section[]): Section[] {
  // get source of P => Q
  const transitiveColumnToColumnMap = _makeColumnToColumnRelationshipMap(sections);
  // get source Q => R
  const transitiveColumnToSectionMap = _makeColumnSectionMap(sections);

  // evaluate P => R for each column
  const options = { transitiveColumnToColumnMap, transitiveColumnToSectionMap };
  return sections.map(s => ({ ...s, columns: _checkColumnTransitivity(s, options) }));
}

/**
 * Function to check whether if each column is a related column (p) (lives in transitiveColumnToColumMap)
 * AND if it is then get the 'principal column' (q) AND get the principal column's transitiveSectionId (r)
 * (lives in transitiveSectionMap) and validate given section is the same a transitive section id found
 *
 * This will ensure we render columns that are not "related" or "uom" fields out of the section where
 * the principal column lives since the move of related and uoms has been passed already as part of
 * story MIQ-20392
 *
 * @param section
 * @param options
 * @returns
 */
function _checkColumnTransitivity(section: Section, options: TransitiveFilteringOptions): Column[] {
  return section.columns.filter(c => {
    // P
    const principalColumnId = options.transitiveColumnToColumnMap[c.columnId];
    // return true if column is not within the transitivity map
    if (principalColumnId == null) return true;
    // Q
    const transitiveSectionId = options.transitiveColumnToSectionMap[principalColumnId];
    // P => Q
    // Q => R
    // :. P => R
    return section.sectionId === transitiveSectionId;
  });
}

/**
 * From an entire catalog creates a map of columnId as key and the section columnId belongs to.
 *
 * @example
 * const section1 = { sectionId: 3, title: 'Foo section', columns: [{columnId: 1}, {columnId:2}]}
 * const section2 = { sectionId: 5, title: 'Bar section', columns: [{columnId: 7}, {columnId:8}]}
 * const makeColumnSectionMap([section]);
 * /*
 *  result would be: {
 *   1: 3,
 *   2: 3,
 *   7: 5,
 *   8: 5
 * }
 * *\/
 *
 * @param catalog
 * @returns
 */
function _makeColumnSectionMap(sections: Section[]): { [key: number]: number } {
  return sections.reduce((acc, s) => Object.assign(acc, ...s.columns.map(c => ({ [c.columnId]: s.sectionId }))), {});
}

/**
 * Creates a dictionary of each related columns within sections objects to use column id as keys and set
 * "principal" column id as value
 *
 * @example
 * const columns = [
 *   { columnId: 1, related: [{columnId: 2}, {columnId:3}]}
 * ];
 *
 * const sections = [ {sectionId: 10, columns }]
 *
 * // when
 * const res = makeColumnToColumnRelationshipMap(sections)
 * // then
 * expect(res).toEqual({ 2:1, 3:1}) // from related (key) to principal (value)
 *
 * @param sections
 * @returns
 */
function _makeColumnToColumnRelationshipMap(sections: Section[]): { [key: number]: number } {
  const allColumns: Column[] = sections.reduce((acc, s) => [...acc, ...s.columns], []);
  const allColumnsWithRelations = allColumns.filter(c => c.related?.length > 0);
  return allColumnsWithRelations.reduce((acc, column) => {
    const unit = column.unit != null ? { [column.unit.columnId]: column.columnId } : {};
    return Object.assign(acc, unit, ...column.related.map(rc => ({ [rc.columnId]: column.columnId })));
  }, {});
}
